import React, { useEffect, useState } from "react";
import Modal from "./Modal.tsx";
import { fetchAdminFeedbackHistory, AdminFeedbackEntry } from "../services/conversations.service.ts";
import styled from "styled-components";
import { parseApiError } from "../utils/errorHandler.ts";

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.5rem;
`;

const HistoryEntry = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  background: #f9f9f9;
  transition: background 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    background: #1f1f2e;
    border-color: #3a3a4e;
  }
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
  transition: border-color 0.3s ease;

  .dark-mode & {
    border-bottom-color: #3a3a4e;
  }
`;

const AdminInfo = styled.div`
  font-weight: 600;
  color: #001f52;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const Timestamp = styled.div`
  font-size: 0.75rem;
  color: #666;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const ContentSection = styled.div`
  margin-top: 0.5rem;
`;

const ContentLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #001f52;
  margin-bottom: 0.25rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const ContentText = styled.div`
  font-size: 0.9rem;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  min-height: 2rem;
  transition: background 0.3s ease, color 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    color: #d0d0d0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

interface AdminFeedbackHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: number;
  type: "comment";
}

export default function AdminFeedbackHistoryModal({
  isOpen,
  onClose,
  conversationId,
  type,
}: AdminFeedbackHistoryModalProps) {
  const [history, setHistory] = useState<AdminFeedbackEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && conversationId) {
      setLoading(true);
      setError(null);
      fetchAdminFeedbackHistory(conversationId)
        .then((res) => {
          const filtered = res.data.filter((entry) => {
            return entry.comment && entry.comment.trim() !== "";
          });
          setHistory(filtered);
        })
        .catch((err) => {
          setError(parseApiError(err));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, conversationId, type]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("da-DK", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const title = "Alle admin feedback";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {loading && <LoadingState>Indlæser historik...</LoadingState>}
      {error && <div style={{ color: "#d32f2f", padding: "1rem" }}>{error}</div>}
      {!loading && !error && (
        <>
          {history.length === 0 ? (
            <EmptyState>Ingen feedback fundet</EmptyState>
          ) : (
            <HistoryList>
              {history.map((entry) => (
                <HistoryEntry key={entry.adminId}>
                  <EntryHeader>
                    <AdminInfo>{entry.adminName || `Admin ID: ${entry.adminId}`}</AdminInfo>
                    <Timestamp>Opdateret {formatDate(entry.updatedAt)}</Timestamp>
                  </EntryHeader>
                  <ContentSection>
                    <ContentLabel>Kommentar:</ContentLabel>
                    <ContentText>{entry.comment || "(Ingen kommentar)"}</ContentText>
                    <ContentLabel>Vurdering (1-5):</ContentLabel>
                    <ContentText>{entry.rating != null ? entry.rating : "(Ikke angivet)"}</ContentText>
                    <ContentLabel>Brug til træning?:</ContentLabel>
                    <ContentText>
                      {entry.useToTrain === null || entry.useToTrain === undefined
                        ? "(Ikke angivet)"
                        : entry.useToTrain
                        ? "Ja"
                        : "Nej"}
                    </ContentText>
                  </ContentSection>
                </HistoryEntry>
              ))}
            </HistoryList>
          )}
        </>
      )}
    </Modal>
  );
}
